/**
 * Premium Cart Drawer v2 — Production-Quality
 *
 * REQUIRED CONTRACT:
 * - customElements.define('cart-drawer', CartDrawer)
 * - open() / close() / renderContents() / getSectionsToRender() / setActiveElement()
 * - #cart-icon-bubble click opens drawer
 * - product-form.js calls renderContents(parsedState)
 * - getSectionsToRender() returns { id: 'cart-drawer', selector: '#CartDrawer' }
 * - overlay is OUTSIDE #CartDrawer so innerHTML replacement won't nuke it
 */

class CartDrawer extends HTMLElement {
  constructor() {
    super();
    this.activeElement = null;

    this.setHeaderCartIconAccessibility();
    this.bindOverlayClose();
    this.bindCloseButtons();
    this.bindEscClose();

    if (typeof this.initUpsellButtons === 'function') {
      this.initUpsellButtons();
    }
    if (typeof this.updateFreeShippingBar === 'function') {
      this.updateFreeShippingBar();
    }
    if (typeof this.updateEmptyState === 'function') {
      this.updateEmptyState();
    }
  }

  /* ========================================
     HEADER CART ICON BINDING
     ======================================== */
  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;

    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');

    if (cartLink.dataset.pcdCartBound === 'true') return;
    cartLink.dataset.pcdCartBound = 'true';

    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });

    cartLink.addEventListener('keydown', (event) => {
      if (event.code && event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  /* ========================================
     OPEN
     ======================================== */
  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);

    setTimeout(() => {
      this.classList.add('animate', 'active', 'is-open');
    });

    document.body.classList.add('overflow-hidden');
    document.documentElement.classList.add('pcd-cart-open');

    this.bindOverlayClose();
    this.bindCloseButtons();

    const shell = this.querySelector('#CartDrawer');
    if (shell && typeof trapFocus === 'function') {
      trapFocus(shell);
    }

    if (typeof this.updateFreeShippingBar === 'function') {
      this.updateFreeShippingBar();
    }
    if (typeof this.updateEmptyState === 'function') {
      this.updateEmptyState();
    }
  }

  /* ========================================
     CLOSE
     ======================================== */
  close() {
    this.classList.remove('active', 'is-open');
    document.body.classList.remove('overflow-hidden');
    document.documentElement.classList.remove('pcd-cart-open');

    if (typeof removeTrapFocus === 'function') {
      removeTrapFocus(this.activeElement);
    }
  }

  /* ========================================
     ACTIVE ELEMENT
     ======================================== */
  setActiveElement(element) {
    this.activeElement = element;
  }

  /* ========================================
     SECTIONS CONTRACT (called by product-form.js)
     ======================================== */
  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    const parsed = new DOMParser().parseFromString(html, 'text/html');
    const element = parsed.querySelector(selector);
    return element ? element.innerHTML : '';
  }

  /* ========================================
     RENDER CONTENTS (called by product-form.js after add-to-cart)
     ======================================== */
  renderContents(parsedState) {
    this.productId = parsedState.id;

    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);

      if (!sectionElement || !parsedState.sections || !parsedState.sections[section.id]) return;

      sectionElement.innerHTML = this.getSectionInnerHTML(
        parsedState.sections[section.id],
        section.selector
      );
    });

    // Re-bind everything after DOM replacement, then open
    setTimeout(() => {
      this.bindOverlayClose();
      this.bindCloseButtons();

      if (typeof this.initUpsellButtons === 'function') {
        this.initUpsellButtons();
      }
      if (typeof this.updateFreeShippingBar === 'function') {
        this.updateFreeShippingBar();
      }
      if (typeof this.updateEmptyState === 'function') {
        this.updateEmptyState();
      }

      this.open();
    });
  }

  /* ========================================
     OVERLAY CLOSE
     ======================================== */
  bindOverlayClose() {
    const overlay = this.querySelector('#CartDrawer-Overlay');
    if (!overlay || overlay.dataset.pcdBound === 'true') return;

    overlay.dataset.pcdBound = 'true';
    overlay.addEventListener('click', (event) => {
      event.preventDefault();
      this.close();
    });

    // Fallback: if user clicks directly on cart-drawer tag (which covers viewport)
    if (this.dataset.pcdRootBound !== 'true') {
      this.dataset.pcdRootBound = 'true';
      this.addEventListener('click', (event) => {
        if (event.target === this || event.target === overlay) {
          event.preventDefault();
          this.close();
        }
      });
    }

    // Prevent clicks inside shell from bubbling
    const shell = this.querySelector('#CartDrawer');
    if (shell && shell.dataset.pcdShellBound !== 'true') {
      shell.dataset.pcdShellBound = 'true';
      shell.addEventListener('click', (event) => {
        event.stopPropagation();
      });
    }
  }

  /* ========================================
     CLOSE BUTTONS
     ======================================== */
  bindCloseButtons() {
    this.querySelectorAll('[data-cart-drawer-close], .drawer__close, .pcd-close').forEach((button) => {
      if (button.dataset.pcdBound === 'true') return;

      button.dataset.pcdBound = 'true';
      button.addEventListener('click', (event) => {
        event.preventDefault();
        this.close();
      });
    });
  }

  /* ========================================
     ESC CLOSE
     ======================================== */
  bindEscClose() {
    if (this.dataset.pcdEscBound === 'true') return;
    this.dataset.pcdEscBound = 'true';

    document.addEventListener('keyup', (event) => {
      if (event.code && event.code.toUpperCase() === 'ESCAPE') {
        this.close();
      }
    });
  }

  /* ========================================
     UPSELL BUTTONS
     ======================================== */
  initUpsellButtons() {
    this.querySelectorAll('.js-upsell-add:not([data-pcd-init])').forEach((button) => {
      button.setAttribute('data-pcd-init', 'true');
      button.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        this.handleUpsellAdd(button);
      });
    });
  }

  /* ========================================
     UPSELL ADD TO CART
     ======================================== */
  handleUpsellAdd(button) {
    const variantId = button.dataset.variantId;
    if (!variantId || button.disabled) return;

    button.disabled = true;
    button.classList.add('pcd-upsell__add-btn--adding');

    const formData = new FormData();
    formData.append('id', variantId);
    formData.append('quantity', 1);
    formData.append(
      'sections',
      this.getSectionsToRender().map((s) => s.id)
    );
    formData.append('sections_url', window.location.pathname);

    fetch(`${routes.cart_add_url}`, {
      method: 'POST',
      headers: { 'X-Requested-With': 'XMLHttpRequest' },
      body: formData,
    })
      .then((r) => r.json())
      .then((response) => {
        if (response.status) {
          console.error('Upsell add error:', response.message);
          button.disabled = false;
          button.classList.remove('pcd-upsell__add-btn--adding');
          return;
        }

        // Success feedback
        button.classList.remove('pcd-upsell__add-btn--adding');
        button.classList.add('pcd-upsell__add-btn--added');
        button.innerHTML = '<svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M2 7l4 4 6-6"/></svg>';

        setTimeout(() => {
          button.classList.remove('pcd-upsell__add-btn--added');
          button.disabled = false;
          button.innerHTML = '<svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"><line x1="8" y1="3" x2="8" y2="13"/><line x1="3" y1="8" x2="13" y2="8"/></svg>';
        }, 2000);

        // Re-render sections (only replaces #CartDrawer innerHTML, not overlay)
        this.getSectionsToRender().forEach((section) => {
          const el = section.selector
            ? document.querySelector(section.selector)
            : document.getElementById(section.id);
          if (!el || !response.sections[section.id]) return;
          el.innerHTML = this.getSectionInnerHTML(response.sections[section.id], section.selector);
        });

        publish(PUB_SUB_EVENTS.cartUpdate, {
          source: 'cart-drawer-upsell',
          productVariantId: variantId,
          cartData: response,
        });

        // Re-init after DOM update
        setTimeout(() => {
          this.bindCloseButtons();
          this.initUpsellButtons();
          this.updateFreeShippingBar();
          this.updateEmptyState();
        }, 50);
      })
      .catch((error) => {
        console.error('Upsell add failed:', error);
        button.disabled = false;
        button.classList.remove('pcd-upsell__add-btn--adding');
      });
  }

  /* ========================================
     FREE SHIPPING PROGRESS
     Threshold is in major units (e.g. 2500 EGP)
     cart.total_price is in cents (e.g. 250000)
     ======================================== */
  updateFreeShippingBar() {
    const isEnabled = this.dataset.freeShippingEnabled === 'true';
    if (!isEnabled) return;

    const thresholdMajor = parseInt(this.dataset.freeShippingThreshold, 10);
    if (!thresholdMajor || thresholdMajor <= 0) return;

    const thresholdCents = thresholdMajor * 100;

    fetch(`${routes.cart_url}.js`, { credentials: 'same-origin' })
      .then((res) => res.json())
      .then((cart) => {
        const subtotalCents = cart.total_price;
        const remainingCents = Math.max(thresholdCents - subtotalCents, 0);
        const progress = Math.min((subtotalCents / thresholdCents) * 100, 100);

        const bar = this.querySelector('.pcd-free-ship__bar');
        const msg = this.querySelector('.pcd-free-ship__msg');

        if (bar) bar.style.width = progress + '%';

        if (msg) {
          const wrapper = this.querySelector('.pcd-free-ship');
          if (remainingCents <= 0) {
            if (wrapper) wrapper.classList.add('pcd-free-ship--success');
            const text = this.dataset.freeShippingSuccessText || 'You unlocked FREE shipping';
            msg.innerHTML = '<span class="pcd-free-ship__success"><svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><path d="M2 8l4 4 8-8"/></svg> ' + text + '</span>';
          } else {
            if (wrapper) wrapper.classList.remove('pcd-free-ship--success');
            const formatted = this.formatMoney(remainingCents);
            const template = this.dataset.freeShippingRemainingText || 'Add [amount] more to get FREE shipping';
            msg.innerHTML = '<span class="pcd-free-ship__remaining">' + template.replace('[amount]', '<strong>' + formatted + '</strong>') + '</span>';
          }
        }

        // Update count
        const countEl = this.querySelector('#CartDrawer-ItemCount');
        if (countEl) {
          countEl.textContent = cart.item_count;
          countEl.classList.toggle('pcd-hidden', cart.item_count === 0);
        }
      })
      .catch(() => {});
  }

  /* ========================================
     EMPTY STATE
     ======================================== */
  updateEmptyState() {
    fetch(`${routes.cart_url}.js`, { credentials: 'same-origin' })
      .then((res) => res.json())
      .then((cart) => {
        const isEmpty = cart.item_count === 0;
        this.classList.toggle('is-empty', isEmpty);

        const emptyEl = this.querySelector('#CartDrawer-EmptyState');
        const itemsEl = this.querySelector('cart-drawer-items');
        const footerEl = this.querySelector('.pcd-cart__footer');
        const countEl = this.querySelector('#CartDrawer-ItemCount');

        if (emptyEl) emptyEl.classList.toggle('pcd-hidden', !isEmpty);
        if (itemsEl) itemsEl.classList.toggle('is-empty', isEmpty);
        if (footerEl) footerEl.classList.toggle('pcd-hidden', isEmpty);
        if (countEl) {
          countEl.textContent = cart.item_count;
          countEl.classList.toggle('pcd-hidden', isEmpty);
        }
      })
      .catch(() => {});
  }

  /* ========================================
     FORMAT MONEY
     ======================================== */
  formatMoney(cents) {
    const currency = (Shopify.currency && Shopify.currency.active) || 'USD';
    try {
      return new Intl.NumberFormat(Shopify.locale || 'en', {
        style: 'currency',
        currency: currency,
      }).format(cents / 100);
    } catch (e) {
      return (cents / 100).toFixed(2);
    }
  }
}

if (!customElements.get('cart-drawer')) {
  customElements.define('cart-drawer', CartDrawer);
}

/* ========================================
   CartDrawerItems — extends CartItems
   ======================================== */
class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }
}

if (!customElements.get('cart-drawer-items')) {
  customElements.define('cart-drawer-items', CartDrawerItems);
}
